import { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { InfoTip } from '../../../components/ui/InfoTip';
import type { ResearchLead } from '../../../api/salesResearch';

/**
 * Leads are capped at three. Each one adds two grounded research calls, and a
 * brief covering more people than that stops being a four-minute read.
 */
const MAX_LEADS = 3;

interface Props {
  onSubmit: (data: {
    companyName: string;
    companyDomain?: string;
    leads?: ResearchLead[];
    deep?: boolean;
  }) => void;
  submitting: boolean;
  disabled?: boolean;
  disabledReason?: string;
  /** Stops the run that is blocking this form. Only passed while one is in flight. */
  onCancelInFlight?: () => void;
  cancelling?: boolean;
  /** Inside the "New research" dialog: the dialog carries the title, so the form drops its own header and border. */
  bare?: boolean;
}

export const RESEARCH_BLURB =
  'Researches the company across Perplexity and Claude, cross-checks what they each found, and writes a two-page brief.';

export function NewResearchForm({
  onSubmit,
  submitting,
  disabled,
  disabledReason,
  onCancelInFlight,
  cancelling,
  bare = false,
}: Props) {
  const [companyName, setCompanyName] = useState('');
  const [companyDomain, setCompanyDomain] = useState('');
  const [leads, setLeads] = useState<ResearchLead[]>([]);
  const [deep, setDeep] = useState(true);

  const canSubmit = companyName.trim().length > 0 && !submitting && !disabled;

  /**
   * Company names are usually pasted, and what gets pasted often carries the
   * website with it ("Lonza Group (https://www.lonza.com/)"). Splitting it here
   * keeps the research target clean and fills the domain for free.
   *
   * On paste only. Run on every keystroke it rewrote the field mid-word: typing
   * the same thing by hand, the domain was stripped the moment ".com" completed
   * and the closing bracket then landed on nothing, leaving runs named
   * "Lonza ()".
   */
  const pasteCompany = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text');
    const { name, domain } = splitPastedCompany(pasted);
    if (name === pasted.trim()) return; // Nothing to split — let the paste land normally.

    e.preventDefault();
    setCompanyName(name);
    if (domain && !companyDomain.trim()) setCompanyDomain(domain);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const named = leads.filter((l) => l.name.trim().length > 0);
    onSubmit({
      companyName: companyName.trim(),
      ...(companyDomain.trim() ? { companyDomain: normaliseDomain(companyDomain) } : {}),
      ...(named.length > 0 ? { leads: named } : {}),
      deep,
    });
  };

  const updateLead = (i: number, patch: Partial<ResearchLead>) => {
    setLeads((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  };

  return (
    <form onSubmit={submit} className={bare ? 'space-y-4' : 'rounded-lg border border-border bg-surface-primary p-4 space-y-4'}>
      {!bare && (
        <div>
          <h2 className="text-body-md font-semibold">New research</h2>
          <p className="text-body-sm text-text-tertiary">{RESEARCH_BLURB}</p>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-body-sm font-medium">Company</span>
          <input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            onPaste={pasteCompany}
            placeholder="Acme Group"
            className="mt-1 w-full rounded-md border border-border bg-surface-primary px-3 py-2 text-body-sm"
          />
        </label>
        <label className="block">
          <span className="text-body-sm font-medium">
            Domain <span className="font-normal text-text-tertiary">(optional)</span>
          </span>
          <input
            value={companyDomain}
            onChange={(e) => setCompanyDomain(e.target.value)}
            placeholder="Found automatically"
            className="mt-1 w-full rounded-md border border-border bg-surface-primary px-3 py-2 text-body-sm"
          />
          {/* The domain is what makes the CRM match and the org-chart lookup
              reliable, but nobody starting a run has it to hand, so the run
              looks it up itself and this field is only an override. */}
          <span className="text-body-xs text-text-tertiary">
            Left blank, the run finds it in the CRM or Apollo. Fill it in only to override.
          </span>
        </label>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-body-sm font-medium">
            Leads <span className="font-normal text-text-tertiary">(optional, up to {MAX_LEADS})</span>
          </span>
          {leads.length < MAX_LEADS && (
            <button
              type="button"
              onClick={() => setLeads((p) => [...p, { name: '' }])}
              className="rounded border border-border px-2 py-0.5 text-body-xs hover:bg-surface-secondary"
            >
              Add lead
            </button>
          )}
        </div>

        {leads.map((lead, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={lead.name}
              onChange={(e) => updateLead(i, { name: e.target.value })}
              placeholder="Full name"
              className="flex-1 rounded-md border border-border bg-surface-primary px-3 py-1.5 text-body-sm"
            />
            <input
              value={lead.title ?? ''}
              onChange={(e) => updateLead(i, { title: e.target.value })}
              placeholder="Title"
              className="flex-1 rounded-md border border-border bg-surface-primary px-3 py-1.5 text-body-sm"
            />
            <button
              type="button"
              onClick={() => setLeads((p) => p.filter((_, idx) => idx !== i))}
              className="rounded border border-border px-2 text-body-xs text-text-tertiary hover:bg-surface-secondary"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1.5">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={deep} onChange={(e) => setDeep(e.target.checked)} />
          <span className="text-body-sm">Add the extended research pass</span>
        </label>
        <InfoTip label="About the extended pass">
          An extra wider-searching pass over the company, on top of the recent-events, revenue and
          operating-model passes that always run. Better coverage, and a meaningful share of the
          cost; leave it off for a quicker look.
        </InfoTip>
      </div>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!canSubmit} isLoading={submitting}>
          {submitting ? 'Starting…' : 'Run research'}
        </Button>
        {disabled && disabledReason && (
          <span className="text-body-sm text-text-tertiary">{disabledReason}</span>
        )}
        {disabled && onCancelInFlight && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancelInFlight}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling…' : 'Cancel it'}
          </Button>
        )}
      </div>
    </form>
  );
}

/**
 * Pulls a website out of a company name, wherever in the string it sits, and
 * returns the name without it. Anything that is not recognisably a URL or a
 * domain is left alone — company names contain dots too ("Bosch B.V.").
 */
function splitPastedCompany(value: string): { name: string; domain: string } {
  const match = value.match(/(?:https?:\/\/)[^\s)]+|(?:\b[\w-]+(?:\.[\w-]+)*\.(?:com|net|org|io|ai|co|eu|be|nl|de|fr|uk|ch|es|it|se|dk|no|fi)\b)(?:\/\S*)?/i);
  if (!match) return { name: value, domain: '' };

  const name = value.replace(match[0], '').replace(/\(\s*\)/g, '').replace(/\s{2,}/g, ' ').trim();
  return { name: name || value.trim(), domain: normaliseDomain(match[0]) };
}

function normaliseDomain(input: string): string {
  return input.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
}
